import { OFFER_TYPE_HOTEL } from '~/consts/offerTypes';
import { ROOM_RATE_FLASH_TYPE } from '~/consts/ratePlans';

interface AutofillPackageResult extends Record<string, unknown> {
  deal_option_name: string;
  name: string;
  fk_room_type_id: string;
  fk_room_rate_id: string;
  package_options: Array<{ fk_room_rate_id: string; name: string }>;
  room_occupancy: string;
  flexible_nights: boolean;
  status: App.Package['status'];
  regions: Array<string>;
  number_of_nights: number;
  max_extra_nights: number;
}

type PackageWithAccommodationFields = App.Package & {
  max_extra_nights?: number;
  number_of_nights?: number;
};

const LEGACY_AUTOFILL_ROOM_OCCUPANCY = 'Two Adults';
const LEGACY_AUTOFILL_NUMBER_OF_NIGHTS = 1;
const LEGACY_AUTOFILL_MAX_EXTRA_NIGHTS = 29;
const LEGACY_AUTOFILL_REGIONS = ['world'] as const;

function getPackageOptionName(roomRate: App.RoomRate) {
  const { rate_plan: ratePlan } = roomRate;

  return (
    ratePlan.name +
    (ratePlan.rate_plan_code ? ` (${ratePlan.rate_plan_code})` : '') +
    (ratePlan.status === 'hidden' ? ' [hidden]' : '')
  );
}

function buildPackageOptions(roomRates: Array<App.RoomRate>): AutofillPackageResult['package_options'] {
  return roomRates.map((roomRate) => ({
    fk_room_rate_id: roomRate.id,
    name: getPackageOptionName(roomRate),
  }));
}

export function filterFlashRoomRates(roomRates: Array<App.RoomRate>): Array<App.RoomRate> {
  return roomRates.filter((rate) => rate.rate_plan.product_type === ROOM_RATE_FLASH_TYPE);
}

export function buildAutofillPackageValues({
  offerPackage,
  property,
  offerType,
}: {
  offerPackage: App.Package;
  property?: App.Property;
  offerType?: App.OfferType;
}): AutofillPackageResult | null {
  if (!property?.room_types?.length || offerType !== OFFER_TYPE_HOTEL) {
    return null;
  }

  const dealOptionName = offerPackage.deal_option_name?.toLowerCase() ?? '';
  const roomType = [...property.room_types]
    .sort((a, b) => b.name.length - a.name.length)
    .find((propertyRoomType) => dealOptionName.includes(propertyRoomType.name.toLowerCase()));

  if (!roomType) {
    return null;
  }

  const roomRates = filterFlashRoomRates(roomType.room_rates ?? []);
  const packageFields = offerPackage as PackageWithAccommodationFields;

  if (roomRates.length === 0) {
    return null;
  }

  return {
    deal_option_name: roomType.name,
    name: roomType.name,
    fk_room_type_id: roomType.id,
    fk_room_rate_id: roomRates[0].id,
    package_options: buildPackageOptions(roomRates),
    room_occupancy: LEGACY_AUTOFILL_ROOM_OCCUPANCY,
    flexible_nights: true,
    status: 'draft',
    regions: [...LEGACY_AUTOFILL_REGIONS],
    number_of_nights: packageFields.number_of_nights ?? LEGACY_AUTOFILL_NUMBER_OF_NIGHTS,
    max_extra_nights: packageFields.max_extra_nights ?? LEGACY_AUTOFILL_MAX_EXTRA_NIGHTS,
  };
}
